import React from 'react';
import Tilt from 'react-parallax-tilt';
import { CloseOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { Ghost } from 'lucide-react';
import './TokenCardOverlay.css';

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 0.4, transition: { duration: 0.25, ease: 'easeInOut' } },
    exit:   { opacity: 0, transition: { duration: 0.2, ease: 'easeInOut' } }
};

const cardVariants = {
    hidden:  { opacity: 0, scale: 0.9, y: 40 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 140,
            damping: 20,
            mass: 0.6
        }
    },
    exit: {
        opacity: 0,
        scale: 0.9,
        y: -40,
        transition: { duration: 0.2, ease: 'easeIn' }
    }
};

const ghostVariant = i => ({
    opacity: [0.2, 0.5, 0.2],
    scale:   [0.8, 1, 0.8],
    transition: {
        duration: 4 + i,
        ease: 'easeInOut',
        repeat: Infinity,
        delay: i * 0.7
    }
});

const floatVariant = {
    animate: {
        y: [0, -15, 0],
        transition: { duration: 3, ease: 'easeInOut', repeat: Infinity }
    }
};

export default function TokenCardOverlay({ visible, onClose, foundTokenInDb }) {
    return (
        <AnimatePresence>
            {visible && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <motion.div
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                        onClick={onClose}
                        variants={backdropVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    />

                    <motion.div
                        className="relative z-10"
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        style={{ willChange: 'transform, opacity' }}
                    >
                        <Tilt
                            glareEnable
                            glareMaxOpacity={0.1}
                            glareColor="#ffffff"
                            glareBorderRadius="1.5rem"
                            perspective={900}
                            tiltMaxAngleX={10}
                            tiltMaxAngleY={10}
                            transitionSpeed={400}
                            className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-white/10 bg-[#070A12]/90 p-7 shadow-[0_24px_80px_-32px_rgba(0,0,0,0.95)] backdrop-blur-xl"
                        >
                            <div className="noise-overlay" />
                            <div className="absolute inset-0 rounded-3xl border border-cyan-400/20 blur-[1px] pointer-events-none" />

                            <CloseOutlined
                                onClick={onClose}
                                className="absolute top-4 right-4 text-white text-xl cursor-pointer z-20 hover:scale-110 transition-transform duration-150"
                            />

                            <div className="relative z-10 text-white text-center">
                                {foundTokenInDb ? (
                                    <>
                                        {foundTokenInDb.logoUrl && ['tl', 'tr', 'bc'].map((pos, i) => (
                                            <motion.img
                                                key={pos}
                                                src={foundTokenInDb.logoUrl}
                                                alt=""
                                                className={`ghost-logo rounded-full ghost-${pos}`}
                                                {...ghostVariant(i)}
                                            />
                                        ))}

                                        <h2 className="text-2xl font-bold mb-1">{foundTokenInDb.name}</h2>
                                        <p className="text-gray-400 text-sm mb-4">({foundTokenInDb.symbol})</p>

                                        {foundTokenInDb.logoUrl && (
                                            <motion.img
                                                src={foundTokenInDb.logoUrl}
                                                alt={`${foundTokenInDb.name} logo`}
                                                className="main-logo mx-auto rounded-full mb-5 object-cover bg-transparent mix-blend-multiply shadow-md"
                                                variants={floatVariant}
                                                initial="animate"
                                                animate="animate"
                                            />
                                        )}

                                        <div
                                            className="mt-6 px-4 py-4 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 shadow-inner transform transition-transform duration-300 hover:scale-[1.02]"
                                            style={{ transformStyle: 'preserve-3d' }}
                                        >
                                            <ul className="space-y-2 text-sm text-left text-gray-200">
                                                <li>
                                                    <span className="font-semibold text-gray-400">ID:</span>
                                                    <span className="ml-2 break-all">{foundTokenInDb.id}</span>
                                                </li>
                                                <li>
                                                    <span className="font-semibold text-gray-400">Адрес:</span>
                                                    <span className="ml-2 break-all">{foundTokenInDb.address}</span>
                                                </li>
                                                <li>
                                                    <span className="font-semibold text-gray-400">Blockchain:</span>
                                                    <span className="ml-2">{foundTokenInDb.blockchain}</span>
                                                </li>
                                                <li>
                                                    <span className="font-semibold text-gray-400">Источник:</span>
                                                    <span className="ml-2">{foundTokenInDb.source || '—'}</span>
                                                </li>
                                            </ul>
                                        </div>

                                    </>
                                ) : (
                                    <div className="py-12 flex flex-col items-center justify-center space-y-3">
                                        <Ghost className="w-12 h-12 text-gray-400" />
                                        <h2 className="text-xl font-semibold text-gray-200">Токен не найден</h2>
                                        <p className="text-sm text-gray-400 px-4">Проверьте адрес или попробуйте снова.</p>
                                    </div>
                                )}
                            </div>
                        </Tilt>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
